import type { InputHTMLAttributes } from 'react';
import type { App } from '@hangar/shared';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { FrameworkBadge } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BitrisePanel } from '@/features/source-control/BitrisePanel';
import { nativeSetupNote } from '@/features/app-page/appPageModel';
import { useSeesProviders } from '@/lib/useAudience';
import type { EditAppField } from './appFormModel';
import { BitriseAppField } from './BitriseAppField';
import { useEditAppForm } from './useEditAppForm';

interface Props {
  app: App | null;
  onOpenChange: (open: boolean) => void;
}

type EditState = ReturnType<typeof useEditAppForm>;

/**
 * Design 29 · "Edit app settings": name, working directory, config file path, EAS project id and, for a native app on
 * Bitrise, the Bitrise app (`PATCH /apps/{id}`, If-Match), each with its one-line hint. The provider fields are for
 * platform operators only (SPEC decision 23). Slug and framework are
 * immutable; the repository and the release branch of the design are not settable through the API yet.
 */
export function EditAppSheet({ app, onOpenChange }: Props) {
  return (
    <Sheet open={Boolean(app)} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="edit-app-sheet" className="w-full gap-0 p-0" aria-describedby="edit-app-desc">
        {app ? <EditAppForm key={app.id} app={app} onDone={() => onOpenChange(false)} /> : null}
      </SheetContent>
    </Sheet>
  );
}

const MONO_INPUT: InputHTMLAttributes<HTMLInputElement> = { spellCheck: false, className: 'font-mono sm:text-[13px]' };

interface TextFieldProps {
  f: EditState;
  field: EditAppField;
  label: string;
  hint: string;
  testId: string;
  maxLength: number;
  required?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
}

/** One text field of the sheet: `edit-app-<field>` ties its label, hint and message together. */
function EditTextField({ f, field, label, hint, testId, maxLength, required, inputProps }: TextFieldProps) {
  const id = `edit-app-${field}`;
  const error = f.errorFor(field);
  return (
    <FormField id={id} label={label} required={required} hint={hint} error={error}>
      <Input data-testid={testId} value={f.values[field]} maxLength={maxLength} onChange={(e) => f.set(field, e.target.value)} {...inputProps} {...fieldA11y(id, error, 'hint')} />
    </FormField>
  );
}

function EditAppHeader({ app }: { app: App }) {
  return (
    <SheetHeader>
      <SheetTitle>Edit app settings</SheetTitle>
      <SheetDescription id="edit-app-desc" className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="font-mono">{app.slug}</span>
        <FrameworkBadge framework={app.framework} />
        <span>slug and framework are fixed</span>
      </SheetDescription>
    </SheetHeader>
  );
}

function EditAppFields({ f }: { f: EditState }) {
  const operator = useSeesProviders();
  return (
    <>
      <EditTextField f={f} field="name" label="Application name" required hint="How the app shows in Hangar; the CLI keeps using the slug" testId="edit-app-name" maxLength={120} inputProps={{ autoComplete: 'off' }} />
      <EditTextField f={f} field="workingDirectory" label="Working directory" required hint="Relative path from the repository root where package.json lives (. for the root)" testId="edit-app-working-directory" maxLength={200} inputProps={MONO_INPUT} />
      {/* The hangar.yml path stays as it is: its sync is after the MVP (docs/mvp-plan.md P9). */}
      {/* The EAS project id is for platform operators only (SPEC decision 23); a client's PATCH never carries it. */}
      {operator ? (
        <EditTextField
        f={f}
        field="easProjectId"
        label="EAS project id"
        hint="Advanced · normally set by Hangar, which creates the project (app page → EAS project). Empty unlinks it"
        testId="edit-app-eas-project-id"
        maxLength={100}
        inputProps={{ ...MONO_INPUT, placeholder: '8f2e6c1a-9b3d-4e5f-a6b7-c8d9e0f1a2b3' }}
        />
      ) : null}
    </>
  );
}

/**
 * The Bitrise part of the sheet: the setup panel (source control) and the Bitrise app link, for platform operators;
 * a client reads that Malible prepares the native builds (SPEC decision 23).
 */
function EditBitriseSection({ app, f }: { app: App; f: EditState }) {
  const operator = useSeesProviders();
  if (!operator) {
    if (!f.showSetup && !f.showBitrise) return null;
    return (
      <p data-testid="edit-app-native-note" className="rounded-lg border border-dashed px-3 py-2.5 text-[13px] text-muted-foreground">
        {nativeSetupNote(Boolean(app.bitriseAppSlug))}
      </p>
    );
  }
  return (
    <>
      {f.showSetup ? <BitrisePanel app={app} compact /> : null}
      {f.showBitrise ? <BitriseAppField idPrefix="edit-app" value={f.values.bitriseAppSlug} onChange={f.setBitrise} error={f.bitriseError} /> : null}
    </>
  );
}

function EditAppFooter({ onDone, pending }: { onDone: () => void; pending: boolean }) {
  return (
    <SheetFooter className="flex-row justify-end border-t">
      <Button variant="outline" onClick={onDone}>
        Cancel
      </Button>
      <Button type="submit" data-testid="edit-app-submit" loading={pending}>
        <Icon name="check_circle" size={18} />
        Save changes
      </Button>
    </SheetFooter>
  );
}

function EditAppForm({ app, onDone }: { app: App; onDone: () => void }) {
  const f = useEditAppForm(app, onDone);
  return (
    <form data-testid="edit-app-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex h-full min-h-0 flex-col" aria-label={`Edit ${app.name}`}>
      <EditAppHeader app={app} />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5">
        <EditAppFields f={f} />
        <EditBitriseSection app={app} f={f} />
        <ProblemAlert error={f.problem} testId="edit-app-problem" />
      </div>
      <EditAppFooter onDone={onDone} pending={f.pending} />
    </form>
  );
}
